import { useRef, useState } from 'react'

import { cn } from '@/lib/utils'

import {
  CORNER_LABELS,
  type Corner,
  type Quad,
  cornerFromPointer,
  cornerPositionText,
  moveCorner,
  nudgeCorner,
} from './corners'

/** One arrow press, in fractions of the scan. 0.5 % is about 4 px on a
 *  720 px-wide drawing and about 12 px on a 2400 px scan — fine enough to
 *  land on a corner, coarse enough to cross the page in a few seconds when
 *  held. Shift multiplies it for the crossing. */
const STEP = 0.005
const COARSE_STEP = 0.04

const ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
}

export interface AlignCanvasProps {
  /** Object URL of the scan as uploaded — photographed, never straightened. */
  url: string
  /** The scan's intrinsic size. Until it is known the canvas draws nothing
   *  but the image, because the box it is drawn in has to be shaped by it. */
  natural: { width: number; height: number } | null
  quad: Quad
  onQuadChange: (quad: Quad) => void
  /** The handle being moved, so the dialog can name it in its caption. */
  activeIndex: number | null
  onActiveIndexChange: (index: number | null) => void
  onNatural: (size: { width: number; height: number }) => void
  onImageError: () => void
  documentTitle: string
  className?: string
}

/**
 * The scan with the four handles on it.
 *
 * The one rule that decides whether this screen is correct: **the image is
 * laid out at its own aspect ratio and fills its box.** A handle's position
 * in the box is then exactly its position in the image, and the fractions
 * this component produces are the fractions the worker wants. With
 * `object-contain` the box would carry letterboxing bars, every handle would
 * be off by their size, and the page would come back shifted — which reads as
 * a bad recognition rather than as the unit error it is. The aspect ratio is
 * therefore taken from the loaded image's natural size and put on the box,
 * and the image is left at its default `fill` semantics inside it.
 */
export function AlignCanvas({
  url,
  natural,
  quad,
  onQuadChange,
  activeIndex,
  onActiveIndexChange,
  onNatural,
  onImageError,
  documentTitle,
  className,
}: AlignCanvasProps) {
  const box = useRef<HTMLDivElement | null>(null)
  const drag = useRef<{ pointerId: number; index: number; grab: Corner } | null>(null)
  // Only keyboard moves are announced: a pointer drag is its own feedback,
  // and a live region firing on every pointermove would be unusable.
  const [announcement, setAnnouncement] = useState('')

  const ratio = natural ? `${natural.width} / ${natural.height}` : undefined

  const moveTo = (index: number, clientX: number, clientY: number, grab: Corner) => {
    const rect = box.current?.getBoundingClientRect()
    if (!rect) return
    // Absolute, not accumulated from deltas, so the handle stays under the
    // cursor even after a clamp at the edge.
    onQuadChange(moveCorner(quad, index, cornerFromPointer(rect, clientX, clientY, grab)))
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center overflow-hidden rounded-sm bg-text-muted p-2',
        className,
      )}
    >
      <div
        ref={box}
        className="relative max-h-[470px] w-full"
        style={ratio ? { aspectRatio: ratio, maxWidth: `calc(470px * ${ratio})` } : undefined}
      >
        <img
          src={url}
          alt={`Scan von ${documentTitle}`}
          draggable={false}
          className="block h-full w-full select-none"
          onLoad={(event) => {
            const width = event.currentTarget.naturalWidth
            const height = event.currentTarget.naturalHeight
            // A "loaded" image of no size is a broken one. Reporting it as a
            // size would give the box the aspect ratio 0/0 and every handle a
            // fraction of nothing.
            if (width > 0 && height > 0) onNatural({ width, height })
            else onImageError()
          }}
          onError={onImageError}
        />

        {natural && (
          <>
            <svg
              viewBox="0 0 1000 1000"
              // Stretched with the image, never letterboxed — same reason the
              // image itself is.
              preserveAspectRatio="none"
              className="pointer-events-none absolute inset-0 h-full w-full"
              // The quadrilateral is a picture of the four handles, and each
              // handle states its own position; a reader gains nothing here.
              aria-hidden="true"
            >
              <polygon
                points={quad.map((c) => `${c.x * 1000},${c.y * 1000}`).join(' ')}
                className="fill-primary/15 stroke-primary-strong"
                // In viewBox units a stroke would be a hairline on a wide scan
                // and a slab on a narrow one, and it would stretch unevenly
                // under preserveAspectRatio="none".
                vectorEffect="non-scaling-stroke"
                strokeWidth={2}
              />
            </svg>

            {quad.map((corner, index) => (
              <button
                key={index}
                type="button"
                data-testid={`align-handle-${index}`}
                aria-label={`Ecke ${CORNER_LABELS[index]}: ${cornerPositionText(corner)}. Mit den Pfeiltasten verschieben.`}
                className={cn(
                  'absolute z-10 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-surface shadow-raised ring-[3px] ring-primary-strong',
                  'touch-none cursor-grab active:cursor-grabbing',
                  activeIndex === index && 'bg-primary-strong ring-surface',
                )}
                style={{ left: `${corner.x * 100}%`, top: `${corner.y * 100}%` }}
                onPointerDown={(event) => {
                  if (event.button !== 0) return
                  const rect = box.current?.getBoundingClientRect()
                  if (!rect) return
                  const pointer = cornerFromPointer(rect, event.clientX, event.clientY)
                  drag.current = {
                    pointerId: event.pointerId,
                    index,
                    // The grab offset keeps the handle from jumping to the
                    // cursor on the first pixel of the drag.
                    grab: { x: pointer.x - corner.x, y: pointer.y - corner.y },
                  }
                  onActiveIndexChange(index)
                  // Capture keeps the drag alive when the pointer leaves the
                  // small handle, which it does immediately. Optional because
                  // jsdom has no implementation and a missing capture only
                  // costs the drag, not the correctness of what it produces.
                  event.currentTarget.setPointerCapture?.(event.pointerId)
                }}
                onPointerMove={(event) => {
                  const active = drag.current
                  if (!active || active.pointerId !== event.pointerId) return
                  moveTo(active.index, event.clientX, event.clientY, active.grab)
                }}
                onPointerUp={(event) => {
                  if (drag.current?.pointerId === event.pointerId) drag.current = null
                }}
                onPointerCancel={() => {
                  drag.current = null
                }}
                onFocus={() => onActiveIndexChange(index)}
                onKeyDown={(event) => {
                  const arrow = ARROWS[event.key]
                  if (!arrow) return
                  // The arrows belong to the focused handle here; a review
                  // screen's own arrow bindings must not also fire.
                  event.preventDefault()
                  event.stopPropagation()
                  const step = event.shiftKey ? COARSE_STEP : STEP
                  const next = nudgeCorner(quad, index, arrow[0] * step, arrow[1] * step)
                  onQuadChange(next)
                  setAnnouncement(
                    `Ecke ${CORNER_LABELS[index]}: ${cornerPositionText(next[index])}`,
                  )
                }}
              >
                <span className="sr-only">{CORNER_LABELS[index]}</span>
              </button>
            ))}

            {activeIndex != null && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute z-10 -translate-x-1/2 translate-y-3 whitespace-nowrap rounded-sm bg-surface px-2 py-0.5 text-caption text-text shadow-raised"
                style={{ left: `${quad[activeIndex].x * 100}%`, top: `${quad[activeIndex].y * 100}%` }}
              >
                {CORNER_LABELS[activeIndex]}
              </span>
            )}
          </>
        )}
      </div>

      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  )
}
